import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';

const NoteTemplateAssociationsPage = ({ templateId: propTemplateId, onClose, onSave, isEmbedded = false }) => {
  const { templateId: paramTemplateId } = useParams();
  const navigate = useNavigate();
  const templateId = propTemplateId || paramTemplateId;

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Data states
  const [healthSystems, setHealthSystems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [providers, setProviders] = useState([]);

  // Static list of specialties for fallback/initial population
  const staticSpecialties = [
    { name: 'Cardiology' },
    { name: 'Dermatology' },
    { name: 'Endocrinology' },
    { name: 'Family Medicine' },
    { name: 'Gastroenterology' },
    { name: 'Internal Medicine' },
    { name: 'Neurology' },
    { name: 'Oncology' },
    { name: 'Orthopedics' },
    { name: 'Pediatrics' },
    { name: 'Psychiatry' },
    { name: 'Radiology' },
    { name: 'Surgery' },
    { name: 'Urology' }
  ];

  // Selection states
  const [selectedHealthSystems, setSelectedHealthSystems] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedClinics, setSelectedClinics] = useState([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState([]);

  // UI states
  const [selectAllHealthSystems, setSelectAllHealthSystems] = useState(false);
  const [selectAllProviders, setSelectAllProviders] = useState(false);

  // Load initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        console.log('Fetching initial data for template:', templateId);

        // Fetch template details
        const templateResponse = await adminApi.get(`/admin/note/templates/${templateId}/`);
        console.log('Template response:', templateResponse.data);
        if (templateResponse.data) {
          const templateData = templateResponse.data;
          setTemplate(templateData);

          // Initialize selection states with existing associations
          setSelectedHealthSystems(templateData.health_systems || []);
          setSelectedDepartments(templateData.departments || []);
          setSelectedClinics(templateData.clinics || []);
          setSelectedSpecialties(templateData.specialties || []);
          setSelectedProviders(templateData.providers || []);

          console.log('Initial selectedHealthSystems:', templateData.health_systems);
          console.log('Initial selectedDepartments:', templateData.departments);
          console.log('Initial selectedClinics:', templateData.clinics);
          console.log('Initial selectedSpecialties:', templateData.specialties);
          console.log('Initial selectedProviders:', templateData.providers);
        }

        // Fetch health systems
        const healthSystemsResponse = await adminApi.getHealthSystems();
        console.log('Health systems response:', healthSystemsResponse.data);
        if (healthSystemsResponse.data.success) {
          setHealthSystems(healthSystemsResponse.data.health_systems);
        }

        // Fetch all specialties from the backend
        try {
          const specialtiesResponse = await adminApi.get('/admin/specialties/');
          console.log('Specialties response:', specialtiesResponse.data);
          if (specialtiesResponse.data.success) {
            const fetchedSpecialties = specialtiesResponse.data.specialties;
            if (fetchedSpecialties && fetchedSpecialties.length > 0) {
              setSpecialties(fetchedSpecialties);
            } else {
              console.log('Fetched specialties list is empty, using static list.');
              setSpecialties(staticSpecialties);
            }
          } else {
            console.error('Error fetching specialties:', specialtiesResponse.data.message);
            // On error, still use the static list as a fallback
            // console.log('Error fetching specialties, using static list as fallback.');
            setSpecialties(staticSpecialties);
          }
        } catch (err) {
          console.error('Error fetching specialties:', err);
          // On error, use the static list as a fallback
          return null
        }

      } catch (err) {
        // console.error('Error fetching initial data:', err);
        setError(`Error: ${err.message}`);
      } finally {
        setLoading(false);
        // console.log('Initial data fetch complete.');
      }
    };

    if (templateId) {
      fetchInitialData();
    }
  }, [templateId]);

  // Fetch departments and clinics when health systems change
  useEffect(() => {
    console.log('selectedHealthSystems changed:', selectedHealthSystems);
    const fetchDependentData = async () => {
      if (selectedHealthSystems.length === 0) {
        console.log('No health systems selected, clearing dependent data.');
        setDepartments([]);
        setClinics([]);
        setProviders([]);
        return;
      }

      // console.log('Fetching dependent data for selected health systems:', selectedHealthSystems);

      const selectedHsObjects = healthSystems.filter(hs => selectedHealthSystems.includes(hs.id));
      const selectedHsIds = selectedHsObjects.map(hs => hs.health_system_id);

      // Fetch departments
      try {
        const departmentPromises = selectedHsIds.map(health_system_id =>
          adminApi.get(`/admin/health-system/${health_system_id}/departments/`)
        );
        const departmentResponses = await Promise.all(departmentPromises);
        console.log('Department responses:', departmentResponses);
        const allDepartments = departmentResponses.flatMap(response =>
          response.data.success ? response.data.departments : []
        );
        setDepartments(allDepartments);
        console.log('Departments set:', allDepartments);
        } catch (err) {
          console.error('Error fetching departments:', err);
          setDepartments([]); // Clear departments on error
        }
        
        // Fetch clinics
        try {
          const clinicPromises = selectedHsIds.map(health_system_id =>
            adminApi.get(`/admin/health-system/${health_system_id}/clinics/`)
          );
          const clinicResponses = await Promise.all(clinicPromises);
          console.log('Clinic responses:', clinicResponses);
          const allClinics = clinicResponses.flatMap(response =>
            response.data.success ? response.data.clinics : []
          );
          setClinics(allClinics);
          console.log('Clinics set:', allClinics);
        } catch (err) {
          console.error('Error fetching clinics:', err);
          setClinics([]); // Clear clinics on error
        }
        
        // Fetch providers
        try {
          const providerPromises = selectedHsIds.map(hsId =>
            adminApi.getHealthSystemProviders(hsId)
          );
          const providerResponses = await Promise.all(providerPromises);
          console.log('Provider responses:', providerResponses);
          const allProviders = providerResponses.flatMap(response =>
            response.data.success ? response.data.providers : []
          );
          setProviders(allProviders);
          console.log('Providers set:', allProviders);

        // Auto-select all providers by default if no providers were initially selected
        // This prevents overriding existing selections when the page loads
        if (selectedProviders.length === 0) {
           setSelectedProviders(allProviders.map(p => p.id));
           setSelectAllProviders(true);
           console.log('Auto-selected all providers.');
        }

      } catch (err) {
        console.error('Error fetching providers:', err);
        setProviders([]); // Clear providers on error
        setSelectAllProviders(false); // Unselect all providers on error
      }
    };

    fetchDependentData();
  }, [selectedHealthSystems]); // Dependency array includes selectedHealthSystems

  // Handle health system selection
  const handleHealthSystemChange = (healthSystemId, checked) => {
    if (checked) {
      setSelectedHealthSystems(prev => [...prev, healthSystemId]);
    } else {
      setSelectedHealthSystems(prev => prev.filter(id => id !== healthSystemId));
      // Clear dependent selections
      setSelectedDepartments(prev => prev.filter(deptId => {
        const dept = departments.find(d => d.id === deptId);
        return dept && dept.health_system_id !== healthSystemId; // Corrected logic
      }));
      setSelectedClinics(prev => prev.filter(clinicId => {
        const clinic = clinics.find(c => c.id === clinicId);
        return clinic && clinic.health_system_id !== healthSystemId; // Corrected logic
      }));
    }
  };

  // Handle select all health systems
  const handleSelectAllHealthSystems = (checked) => {
    setSelectAllHealthSystems(checked);
    if (checked) {
      setSelectedHealthSystems(healthSystems.map(hs => hs.id));
    } else {
      setSelectedHealthSystems([]);
      setSelectedDepartments([]);
      setSelectedClinics([]);
      setSelectedProviders([]);
    }
  };

  // Handle provider selection
  const handleProviderChange = (providerId, checked) => {
    if (checked) {
      setSelectedProviders(prev => [...prev, providerId]);
    } else {
      setSelectedProviders(prev => prev.filter(id => id !== providerId));
      setSelectAllProviders(false);
    }
  };

  // Handle select all providers
  const handleSelectAllProviders = (checked) => {
    setSelectAllProviders(checked);
    if (checked) {
      setSelectedProviders(providers.map(p => p.id));
    } else {
      setSelectedProviders([]);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedHealthSystems.length === 0) {
      setError('Please select at least one health system');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const associationData = {
        template_id: templateId,
        health_systems: selectedHealthSystems,
        departments: selectedDepartments,
        clinics: selectedClinics,
        // Send specialty names instead of IDs to the backend
        specialties: specialties
          .filter(specialty => selectedSpecialties.includes(specialty.id))
          .map(specialty => specialty.name),
        providers: selectedProviders
      };

      // Save template associations using the new POST endpoint
      await adminApi.post(`/admin/note/templates/${templateId}/associations/`, associationData);

      // Navigate back or call onSave callback
      if (isEmbedded && onSave) {
        onSave();
      } else {
        navigate('/admin/note-templates');
      }
    } catch (err) {
      console.error('Error saving associations:', err);
      setError(`Error saving associations: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (isEmbedded && onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading template associations...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={isEmbedded ? '' : 'container-fluid'}>
        {!isEmbedded && (
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">
                  <button
                    className="btn btn-sm btn-light me-2"
                    onClick={handleBack}
                  >
                    <i className="fa fa-arrow-left me-1"></i>
                  </button>
                  Template Associations: {template?.name}
                </h4>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Health Systems Selection */}
            <div className="col-lg-6 mb-4">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title text-primary mb-4">
                    <i className="fas fa-hospital me-2"></i>
                    Health Systems
                  </h5>

                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="selectAllHealthSystems"
                        checked={selectAllHealthSystems}
                        onChange={(e) => handleSelectAllHealthSystems(e.target.checked)}
                      />
                      <label className="form-check-label fw-bold" htmlFor="selectAllHealthSystems">
                        Select All Health Systems
                      </label>
                    </div>
                  </div>

                  <div className="max-height-300 overflow-auto">
                    {healthSystems.map(healthSystem => (
                      <div key={healthSystem.id} className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`hs-${healthSystem.id}`}
                          checked={selectedHealthSystems.includes(healthSystem.id)}
                          onChange={(e) => handleHealthSystemChange(healthSystem.id, e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor={`hs-${healthSystem.id}`}>
                          {healthSystem.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Departments Selection */}
            <div className="col-lg-6 mb-4">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title text-primary mb-4">
                    <i className="fas fa-building me-2"></i>
                    Departments
                  </h5>

                  {selectedHealthSystems.length === 0 ? (
                    <div className="text-muted">
                      <i className="fas fa-info-circle me-2"></i>
                      Select health systems first to see available departments
                    </div>
                  ) : departments.length === 0 ? (
                    <div className="text-muted">
                      <i className="fas fa-info-circle me-2"></i>
                      No departments found for selected health systems
                    </div>
                  ) : (
                    <div className="max-height-300 overflow-auto">
                      {departments.map(department => (
                        <div key={department.id} className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`dept-${department.id}`}
                            checked={selectedDepartments.includes(department.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDepartments(prev => [...prev, department.id]);
                              } else {
                                setSelectedDepartments(prev => prev.filter(id => id !== department.id));
                              }
                            }}
                          />
                          <label className="form-check-label" htmlFor={`dept-${department.id}`}>
                            {department.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Clinics Selection */}
            <div className="col-lg-6 mb-4">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title text-primary mb-4">
                    <i className="fas fa-clinic-medical me-2"></i>
                    Clinics
                  </h5>

                  {selectedHealthSystems.length === 0 ? (
                    <div className="text-muted">
                      <i className="fas fa-info-circle me-2"></i>
                      Select health systems first to see available clinics
                    </div>
                  ) : clinics.length === 0 ? (
                    <div className="text-muted">
                      <i className="fas fa-info-circle me-2"></i>
                      No clinics found for selected health systems
                    </div>
                  ) : (
                    <div className="max-height-300 overflow-auto">
                      {clinics.map(clinic => (
                        <div key={clinic.id} className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`clinic-${clinic.id}`}
                            checked={selectedClinics.includes(clinic.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedClinics(prev => [...prev, clinic.id]);
                              } else {
                                setSelectedClinics(prev => prev.filter(id => id !== clinic.id));
                              }
                            }}
                          />
                          <label className="form-check-label" htmlFor={`clinic-${clinic.id}`}>
                            {clinic.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Specialties Selection */}
            <div className="col-lg-6 mb-4">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title text-primary mb-4">
                    <i className="fas fa-stethoscope me-2"></i>
                    Specialties
                  </h5>

                  <div className="max-height-300 overflow-auto">
                    {specialties.map(specialty => (
                      <div key={specialty.id} className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`specialty-${specialty.id}`}
                          checked={selectedSpecialties.includes(specialty.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSpecialties(prev => [...prev, specialty.id]);
                            } else {
                              setSelectedSpecialties(prev => prev.filter(id => id !== specialty.id));
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor={`specialty-${specialty.id}`}>
                          {specialty.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Providers Selection */}
            <div className="col-12 mb-4">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title text-primary mb-4">
                    <i className="fas fa-user-md me-2"></i>
                    Providers
                  </h5>

                  {selectedHealthSystems.length === 0 ? (
                    <div className="text-muted">
                      <i className="fas fa-info-circle me-2"></i>
                      Select health systems first to see available providers
                    </div>
                  ) : providers.length === 0 ? (
                    <div className="text-muted">
                      <i className="fas fa-info-circle me-2"></i>
                      No providers found for selected health systems
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="selectAllProviders"
                            checked={selectAllProviders}
                            onChange={(e) => handleSelectAllProviders(e.target.checked)}
                          />
                          <label className="form-check-label fw-bold" htmlFor="selectAllProviders">
                            Select All Providers ({providers.length})
                          </label>
                        </div>
                      </div>

                      <div className="row">
                        {providers.map(provider => (
                          <div key={provider.id} className="col-md-4 col-lg-3 mb-2">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`provider-${provider.id}`}
                                checked={selectedProviders.includes(provider.id)}
                                onChange={(e) => handleProviderChange(provider.id, e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor={`provider-${provider.id}`}>
                                {provider.first_name} {provider.last_name}
                                {provider.specialty && (
                                  <small className="text-muted d-block">{provider.specialty}</small>
                                )}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* Submit Buttons */}
            <div className="d-flex gap-2 ml-1">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || selectedHealthSystems.length === 0}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Saving Associations...
                </>
              ) : (
                <>
                  Save Template Associations
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleBack}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
          </div>
        </form>
      </div>

      <style>{`
        .max-height-300 {
          max-height: 300px;
        }
      `}</style>
    </>
  );
};

export default NoteTemplateAssociationsPage;